import React, { useState, useEffect, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
    selectIsAuthenticated,
    selectLoggedInUser,
    selectTokenValid,
    selectLoading,
    initializeFromStorage,
    validateToken,
    setAuthFromStorage
} from "../../redux/slices/userSlices";
import authService from "../../services/auth.service";
import config from "../../config/config";
import { DEBUG_MODE } from "../../config/apiConfig";
import errorHandler from "../../utils/errorHandler";

/**
 * Enhanced Protected Route Component
 * Provides authentication and role-based access control
 */
function ProtectedRoute({ 
    children, 
    requiredRoles = [], 
    requiredPermissions = [],
    fallbackPath = null,
    showLoading = true 
}) {
    const location = useLocation();
    const dispatch = useDispatch();
    
    // Redux state
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const loggedInUser = useSelector(selectLoggedInUser);
    const tokenValid = useSelector(selectTokenValid);
    const loading = useSelector(selectLoading);
    
    // Local state
    const [isValidating, setIsValidating] = useState(true);
    const [authError, setAuthError] = useState(null);
    const [hasRequiredAccess, setHasRequiredAccess] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    /**
     * Check role hierarchy using RBAC system
     * @param {string} userRole - User's role
     * @param {string} requiredRole - Required role
     * @returns {boolean} Role matches or is higher in hierarchy
     */
    const isRoleHierarchyMatch = useCallback(async (userRole, requiredRole) => {
        try {
            // If exact match, allow access
            if (userRole === requiredRole) {
                return true;
            }

            // Use RBAC service to check hierarchy
            const rbacService = (await import('../../services/rbacService')).default;
            const userInfo = authService.getUserInfo();
            
            if (!userInfo || !userInfo.id) {
                return false;
            }

            // Check if user has permission to access this role level
            const userPermissions = await rbacService.getUserPermissions(userInfo.id);
            const hierarchyLevel = userInfo.hierarchy_level || 0;
            
            // Define role hierarchy levels (this should come from backend)
            const roleHierarchy = {
                'super_admin': 10,
                'system_admin': 9,
                'user_manager': 8,
                'hospital_administrator': 7,
                'department_head': 6,
                'consultant_doctor': 5,
                'doctor': 4,
                'nurse_manager': 4,
                'nurse': 3,
                'technician': 2,
                'reception_staff': 2,
                'patient': 1
            };

            const userLevel = roleHierarchy[userRole] || hierarchyLevel || 0;
            const requiredLevel = roleHierarchy[requiredRole] || 0;

            return userLevel >= requiredLevel;
        } catch (error) {
            console.error('Error checking role hierarchy:', error);
            // Fallback to simple string comparison
            return userRole === requiredRole;
        }
    }, []);

    /**
     * Check user permission
     * @param {Object} userPermissions - User's permissions
     * @param {string} permission - Required permission
     * @returns {boolean} Permission granted
     */
    const checkUserPermission = useCallback((userPermissions, permission) => {
        // Handle nested permission objects
        const permissionParts = permission.split('.');
        let current = userPermissions;

        for (const part of permissionParts) {
            if (current && typeof current === 'object' && current[part] !== undefined) {
                current = current[part];
            } else {
                return false;
            }
        }

        return Boolean(current);
    }, []);

    /**
     * Check if user meets role and permission requirements
     * @param {Object} userInfo - User information
     * @param {Array} roles - Required roles
     * @param {Array} permissions - Required permissions
     * @returns {Promise<boolean>} Access granted
     */
    const checkAccessRequirements = useCallback(async (userInfo, roles, permissions) => {
        // If no requirements specified, allow access for authenticated users
        if (roles.length === 0 && permissions.length === 0) {
            return true;
        }

        const userRole = userInfo.role;
        const userPermissions = userInfo.permissions || {};

        DEBUG_MODE && console.log("ProtectedRoute: Checking access requirements", {
            userRole,
            userPermissions,
            requiredRoles: roles,
            requiredPermissions: permissions
        });

        // Check role requirements
        if (roles.length > 0) {
            const roleChecks = await Promise.all(roles.map(async role => {
                // Handle both string roles and role hierarchies
                if (typeof role === 'string') {
                    return userRole === role || await isRoleHierarchyMatch(userRole, role);
                }
                return false;
            }));
            
            const hasRequiredRole = roleChecks.some(result => result);
        
            if (!hasRequiredRole) {
                DEBUG_MODE && console.log("ProtectedRoute: Role requirement not met");
                return false;
            }
        }

        // Check permission requirements using RBAC service
        if (permissions.length > 0) {
            try {
                const rbacService = (await import('../../services/rbacService')).default;
                const permissionChecks = await Promise.all(permissions.map(async permission => {
                    try {
                        const result = await rbacService.checkUserPermission(userInfo.id, permission);
                        return result.success && result.data;
                    } catch (error) {
                        console.error('Error checking permission:', permission, error);
                        // Fallback to local permission check
                        return checkUserPermission(userPermissions, permission);
                    }
                }));

                const hasRequiredPermissions = permissionChecks.every(result => result);

                if (!hasRequiredPermissions) {
                    DEBUG_MODE && console.log("ProtectedRoute: Permission requirement not met");
                    return false;
                }
            } catch (error) {
                console.error('Error checking permissions via RBAC service:', error);
                // Fallback to local permission checking
                const hasRequiredPermissions = permissions.every(permission => {
                    return checkUserPermission(userPermissions, permission);
                });

                if (!hasRequiredPermissions) {
                    DEBUG_MODE && console.log("ProtectedRoute: Permission requirement not met (fallback)");
                    return false;
                }
            }
        }

        DEBUG_MODE && console.log("ProtectedRoute: Access requirements met");
        return true;
    }, [isRoleHierarchyMatch, checkUserPermission]);



    /**
     * Initialize authentication state and validate access
     */
    const initializeAuth = useCallback(async () => {
        // Prevent multiple initializations
        if (isInitialized) {
            DEBUG_MODE && console.log("ProtectedRoute: Already initialized, skipping");
            return;
        }

        try {
            setIsValidating(true);
            setAuthError(null);

            DEBUG_MODE && console.log("ProtectedRoute: Starting authentication initialization...");

            // Initialize Redux state from localStorage
            dispatch(initializeFromStorage());
    
            // Check if user is authenticated via auth service
            const isAuthServiceAuthenticated = authService.isAuthenticated();
            const userInfo = authService.getUserInfo();
            const token = authService.getAccessToken();

            DEBUG_MODE && console.log("ProtectedRoute: Auth check results", {
                isAuthServiceAuthenticated,
                hasUserInfo: !!userInfo,
                hasToken: !!token,
                reduxAuthenticated: isAuthenticated,
                tokenValid
            });

            if (!isAuthServiceAuthenticated || !userInfo || !token) {
                DEBUG_MODE && console.log("ProtectedRoute: User not authenticated");
                setAuthError("Authentication required");
                setIsValidating(false);
                setIsInitialized(true);
                return;
            }

            // Update Redux state with auth service data
            if (!isAuthenticated) {
                DEBUG_MODE && console.log("ProtectedRoute: Updating Redux state with auth data");
                dispatch(setAuthFromStorage({
                    user: userInfo,
                    token: token,
                    refreshToken: authService.getRefreshToken()
                }));
            }

            // Validate token if not already validated recently
            if (tokenValid === null) {
                DEBUG_MODE && console.log("ProtectedRoute: Validating token...");
                
                try {
                    await dispatch(validateToken()).unwrap();
                    DEBUG_MODE && console.log("ProtectedRoute: Token validation successful");
                } catch (error) {
                    DEBUG_MODE && console.log("ProtectedRoute: Token validation failed", error);
                    setAuthError("Session expired");
                    setIsValidating(false);
                    setIsInitialized(true);
                    return;
                }
            } else if (tokenValid === false) {
                DEBUG_MODE && console.log("ProtectedRoute: Token is invalid");
                setAuthError("Session expired");
                setIsValidating(false);
                setIsInitialized(true);
                return;
            }

            // Check role and permission requirements
            const hasAccess = checkAccessRequirements(userInfo, requiredRoles, requiredPermissions);
            setHasRequiredAccess(hasAccess);

            if (!hasAccess) {
                DEBUG_MODE && console.log("ProtectedRoute: Access denied - insufficient permissions");
                setAuthError("Access denied - insufficient permissions");
            } else {
                DEBUG_MODE && console.log("ProtectedRoute: Authentication and authorization successful");
            }

    } catch (error) {
            DEBUG_MODE && console.error("ProtectedRoute: Error during auth initialization", error);
            setAuthError("Authentication error");
            errorHandler.handleApiError(error, { 
                context: 'protected-route',
                showToast: false 
            });
        } finally {
            setIsValidating(false);
            setIsInitialized(true);
            DEBUG_MODE && console.log("ProtectedRoute: Initialization completed");
        }
    }, [dispatch, requiredRoles, requiredPermissions, isAuthenticated, tokenValid, checkAccessRequirements, isInitialized]);

    /**
     * Initialize authentication on mount
     */
    useEffect(() => {
        initializeAuth();
    }, [initializeAuth]);

    /**
     * Get redirect path based on error type
     * @returns {string} Redirect path
     */
    const getRedirectPath = useCallback(() => {
        if (fallbackPath) {
            return fallbackPath;
        }

        if (authError === "Access denied - insufficient permissions") {
            return `${config.baseUrl}access-denied`;
        }

        return `${config.baseUrl}login`;
    }, [fallbackPath, authError]);

    /**
     * Render loading component
     */
    const renderLoading = useCallback(() => {
        if (!showLoading) {
            return null;
        }

        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Verifying authentication...</p>
                </div>
            </div>
        );
    }, [showLoading]);

    /**
     * Render access denied component
     */
    const renderAccessDenied = useCallback(() => {
        return (
            <div className="container mt-5">
                <div className="row justify-content-center">
                    <div className="col-md-6 text-center">
                        <div className="card">
                            <div className="card-body">
                                <h3 className="card-title text-danger">Access Denied</h3>
                                <p className="card-text">
                                    You don't have permission to access this page.
                                </p>
                                <div className="mt-3">
                                    <button 
                                        className="btn btn-primary me-2"
                                        onClick={() => window.history.back()}
                                    >
                                        Go Back
                                    </button>
                                    <button 
                                        className="btn btn-outline-primary"
                                        onClick={() => window.location.href = `${config.baseUrl}dashboard`}
                                    >
                                        Dashboard
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }, []);

    // Show loading while validating
    if (isValidating || loading) {
        DEBUG_MODE && console.log("ProtectedRoute: Showing loading state", { isValidating, loading });
        return renderLoading();
    }

    // Handle authentication errors
    if (authError) {
        DEBUG_MODE && console.log("ProtectedRoute: Auth error", authError);

        // Show access denied page for permission errors
        if (authError === "Access denied - insufficient permissions") {
            return renderAccessDenied();
        }

        // Redirect to login for authentication errors
        return (
            <Navigate 
                to={getRedirectPath()} 
                state={{ 
                    from: location.pathname,
                    error: authError 
                }} 
                replace 
            />
        );
    }

    // Check if user is authenticated and has required access
    if (!isAuthenticated || !hasRequiredAccess) {
        DEBUG_MODE && console.log("ProtectedRoute: User not authenticated or lacks access", {
            isAuthenticated,
            hasRequiredAccess,
            pathname: location.pathname
        });

        return (
            <Navigate 
                to={getRedirectPath()} 
                state={{ 
                    from: location.pathname,
                    error: !isAuthenticated ? "Authentication required" : "Access denied"
                }} 
                replace 
            />
        );
    }

    // User is authenticated and has required access
    DEBUG_MODE && console.log("ProtectedRoute: Rendering protected content", {
        pathname: location.pathname,
        userRole: loggedInUser?.role
    });
    return children;
}

/**
 * Higher-order component for protecting routes with specific roles
 * @param {Array} roles - Required roles
 * @returns {Function} Protected route component
 */
export const withRoles = (roles) => {
    return (Component) => {
        return (props) => (
            <ProtectedRoute requiredRoles={roles}>
                <Component {...props} />
            </ProtectedRoute>
        );
    };
};

/**
 * Higher-order component for protecting routes with specific permissions
 * @param {Array} permissions - Required permissions
 * @returns {Function} Protected route component
 */
export const withPermissions = (permissions) => {
    return (Component) => {
        return (props) => (
            <ProtectedRoute requiredPermissions={permissions}>
                <Component {...props} />
            </ProtectedRoute>
        );
    };
};

/**
 * Admin-only protected route
 */
export const AdminRoute = ({ children }) => (
    <ProtectedRoute requiredRoles={['super_admin', 'admin']}>
        {children}
    </ProtectedRoute>
);

/**
 * Manager and above protected route
 */
export const ManagerRoute = ({ children }) => (
    <ProtectedRoute requiredRoles={['super_admin', 'admin', 'manager']}>
        {children}
    </ProtectedRoute>
);

/**
 * Staff and above protected route
 */
export const StaffRoute = ({ children }) => (
    <ProtectedRoute requiredRoles={['super_admin', 'admin', 'manager', 'staff']}>
        {children}
    </ProtectedRoute>
);

export default ProtectedRoute;
